import React, {useCallback, useEffect, useRef, useState} from 'react';
import {IconCheck, IconEye, IconEyeSlash, IconSpinner, IconTurnBack} from "../../../../components/icons";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {
  createUtilisateur,
  getPartenaires,
  getUtilisateurById,
  getUtilisateurRoles,
  updateUtilisateur,
  resetUserTwoFactor
} from "../../../../components/requests";
import {Partenaire, Utilisateur, UtilisateurRole} from "../../../../components/models";
import {useFormik} from "formik";
import Select from "react-select";
import {toast} from "react-toastify";
import {useAuth} from "../../../contexts/AuthContext";
import TwoFactorSettings from "./components/TwoFactorSettings";

// type du selecteur multiple des partenaires
type Option = {
  value: string;
  label: string;
};

type TwoFactorMethod = 'totp' | 'email';

type TwoFactorData = {
  twoFactorRequired: boolean;
  twoFactorMethod: TwoFactorMethod | null;
  twoFactorEmail: string | null;
};

interface UtilisateurProps {
  nom?: string;
  prenom?: string;
  email?: string;
  nomUtilisateur?: string;
  password?: string;
  confirmPassword?: string;
  utilisateurRole?: string;
  partenaire?: string | [];
  twoFactorRequired?: boolean;
}

// Type pour les données utilisateurs passés en state dans navigate pour les modifications
interface LocationState {
  user: any
}

type UtilisateurErrors = Partial<Record<keyof UtilisateurProps, string>>;

const UtilisateurForm = () => {
  const navigate = useNavigate();
  const location = useLocation()
  const locationState = location.state as LocationState
  const {id} = useParams();
  const {isAdmin, currentUser, setTrigger} = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(!!id);
  const [partenaireLoading, setPartenaireLoading] = useState<boolean>(!!id);
  const [roleLoading, setRoleLoading] = useState<boolean>(!!id);
  const [utilisateurRoles, setUtilisateurRoles] = useState<UtilisateurRole[]>([]);
  const [partenaires, setPartenaires] = useState<Partenaire[]>([]);
  const [utilisateur, setUtilisateur] = useState<Utilisateur | null>(locationState?.user ?? null);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [displayPartenaire, setDisplayPartenaire] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isResetting2FA, setIsResetting2FA] = useState<boolean>(false);
  const [showReset2FAConfirm, setShowReset2FAConfirm] = useState<boolean>(false);
  const [settings2FA, setSettings2FA] = useState<any>({
    twoFactorRequired: null,
    twoFactorMethod: null,
    twoFactorEmail: null,
  });

  // Méthode de validation dynamique
  const validate = (values: UtilisateurProps) => {
    const errors: UtilisateurErrors = {};

    if (!values.nom) {
      errors.nom = 'Ce champ est requis';
    }

    if (!values.prenom) {
      errors.prenom = 'Ce champ est requis';
    }

    if (!values.email) {
      errors.email = 'Ce champ est requis';
    } else if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/i.test(values.email)) {
      errors.email = 'Adresse E-mail invalide';
    }

    if (!values.nomUtilisateur) {
      errors.nomUtilisateur = 'Ce champ est requis';
    }

    // Le mot de passe est obligatoire en mode création uniquement
    if (!values.password && !id) {
      errors.password = 'Ce champ est requis';
    } else if (values.password && values.password.length < 8) {
      errors.password = 'Doit contenir au moins 8 caractères';
    }

    // La confirmation du mot de passe est obligatoire en mode création uniquement
    if (!values.confirmPassword && !id) {
      errors.confirmPassword = 'Ce champ est requis';
    } else if (values.confirmPassword && values.confirmPassword.length < 8) {
      errors.confirmPassword = 'Doit contenir au moins 8 caractères';
    }

    // Si il y a un mot de passe mais aucune confirmation
    if (values.password && !values.confirmPassword) {
      errors.confirmPassword = 'Ce champ est requis';
    }

    // Si il y a un mot de passe et sa confirmation mais que les deux valeurs sont différentes
    if (values.password && values.confirmPassword && values.password !== values.confirmPassword) {
      errors.password = 'Le mot de passe ne correspond pas';
      errors.confirmPassword = 'Le mot de passe ne correspond pas';
    }

    if (!values.utilisateurRole) {
      errors.utilisateurRole = 'Ce champ est requis';
    }

    return errors;
  }

  // Méthode d'obtention des partenaires pour le formulaire partenaire
  const getlistPartenaires = useCallback(() => {
    getPartenaires()
      .then((response) => {
        if (response.status === 200 && response.data !== undefined) {
          setPartenaires(response.data.member);
        }
      })
      .catch((error) => console.error(error))
      .finally(() => {
        setPartenaireLoading(false)
        setIsLoading(false)
      });
  }, []);

  // Méthode d'obtention des roles pour le formulaire utilisateurRole
  const getListUtilisateurRoles = useCallback(() => {
    getUtilisateurRoles()
      .then((response) => {
        if (response.status === 200 && response.data !== undefined) {
          setUtilisateurRoles(response.data.member);
          getlistPartenaires();
        }
      })
      .catch((error) => console.error(error))
      .finally(() => setRoleLoading(false));
  }, [getlistPartenaires]);

  // Méthode d'obtention de l'utilisateur par rapport à l'id dans l'URL du navigateur
  const findUtilisateurById = useCallback(() => {
    if (id !== undefined) {
      getUtilisateurById(id)
        .then((response) => {
          if (response.status === 200 && response.data !== undefined) {
            setUtilisateur(response.data);
          }
        })
        .catch((error) => console.error(error))
        .finally(() => setIsLoading(false));
    }
  }, [id]);

  // Méthode centralisé d'obtention de l'objet dans utilisateurRoles portant le libelle Gestionnaire
  const getGestionnaireValue = useCallback(() => {
    return utilisateurRoles.filter(role => role.partenaire === true);
  }, [utilisateurRoles]);

  // Méthode de formattage des données de partenaire pour pré-remplir le formulaire de modification d'un utilisateur
  const preparePartenaireData = () => {
    if (utilisateur) {
      return utilisateur.partenaires.map(partenaire => {
        return {label: partenaire.partenaire.nom, value: partenaire.partenaire.id.toString()};
      });
    }
  };

  // Méthode de formattage des données de partenaire sur le formulaire
  const partenaireData = partenaires.map(partenaire => {
    return {value: partenaire.id.toString(), label: partenaire.nom}
  });

  // Méthode de soumission du formulaire de création / modification
  const formik = useFormik({
    initialValues: {
      nom: utilisateur ? utilisateur.nom : '',
      prenom: utilisateur ? utilisateur.prenom : '',
      email: utilisateur ? utilisateur.email : '',
      nomUtilisateur: utilisateur ? utilisateur.nomUtilisateur : '',
      password: '',
      confirmPassword: '',
      utilisateurRole: utilisateur ? utilisateur.utilisateurRole.id.toString() : '',
      partenaire: (utilisateur && partenaires.length > 0) ? preparePartenaireData() : '',
      twoFactorRequired: utilisateur ? (utilisateur.twoFactorRequired ?? false) : false,
    },
    enableReinitialize: true,
    validate,
    onSubmit: (values, {setFieldError}) => {
      const gestionnaire = getGestionnaireValue();
      if (!gestionnaire) return;

      // Vérifier si le rôle est parmi la liste ayant des partenaires
      const isGest = gestionnaire.some(
        role => values.utilisateurRole === role.id.toString()
      );

      // Si l'utilisateur est un gestionnaire mais qu'il n'y a pas de valeur dans partenaire
      if (isGest && values.partenaire === '') {
        setFieldError('partenaire', 'Ce champ est requis');
        return;
      }

      setIsSubmitting(true);

      // Réglages 2FA : ceux modifiés dans le formulaire, sinon ceux de l'utilisateur
      const twoFactor: TwoFactorData = settings2FA ?? {
        twoFactorRequired: utilisateur?.twoFactorRequired ?? false,
        twoFactorMethod: utilisateur?.twoFactorMethod ?? null,
        twoFactorEmail: utilisateur?.twoFactorEmail ?? null,
      };

      const payload = {
        nom: values.nom,
        prenom: values.prenom,
        email: values.email,
        password: values.confirmPassword,
        nomUtilisateur: values.nomUtilisateur,
        utilisateurRole: values.utilisateurRole,
        partenaires: (values.partenaire.length > 0 && typeof values.partenaire === "object")
          ? values.partenaire.map((partenaire: Option) => partenaire.value)
          : [],
        ...twoFactor,
      };

      const method = id ? updateUtilisateur(Number(id), payload) : createUtilisateur(payload);
      method
        .then((response) => {
          if (response.status === 200 && response.data !== undefined) {
            if (id && payload.email === currentUser.email) setTrigger(true);
            toast.success(`Utilisateur ${(id) ? 'modifié' : 'créé'}`);
            if (isAdmin) navigate('/parametrage/utilisateurs');
          }
        })
        .catch((error) => {
          if (error.status === 409) toast.error(error.response.data);
          console.error(error);
        })
        .finally(() => setIsSubmitting(false));
    },
  });

  // useRef sur le formulaire pour l'utiliser dans le callback
  const formikRef = useRef(formik);

  // Méthode d'affichage du formulaire des partenaires si roleUtilisateur est sur Gestionnaire
  const verifyRole = useCallback(() => {
    const {utilisateurRole} = formik.values;
    if (utilisateurRole === '') return;

    const gestionnaire = getGestionnaireValue();
    if (!gestionnaire) return;

    // Vérifier si le rôle sélectionné est dans la liste ayant des partenaires
    const isGest = gestionnaire.some(
      role => utilisateurRole === role.id.toString()
    );

    if (isGest) {
      setDisplayPartenaire(true);
    } else {
      setDisplayPartenaire(false);
      formikRef.current.setFieldValue('partenaire', '');
    }
  }, [formik.values, getGestionnaireValue]);

  // useEffect pour avoir les données de utilisateurRoles, partenaires et de l'utilisateur si en mode modification
  useEffect(() => {
    // Si l'utilisateur est un administrateur ou si l'utilisateur modifie son propre compte
    if (isAdmin || currentUser.id === Number(id)) {
      getListUtilisateurRoles();
    } else {
      navigate('/parametrage/utilisateurs');
    }
  }, [
    getListUtilisateurRoles,
    getlistPartenaires,
    findUtilisateurById,
    isAdmin,
    currentUser,
    id,
    navigate
  ]);

  // useEffect de vérification du role durant la modification et afficher ou non le formulaire de partenaire
  useEffect(() => {
    verifyRole();
  }, [verifyRole]);

  const handleReset2FA = () => {
    if (!id) return;
    setIsResetting2FA(true);
    resetUserTwoFactor(Number(id))
      .then(() => {
        toast.success('Authentification à deux facteurs réinitialisée');
        setShowReset2FAConfirm(false);
        // Refresh user data
        findUtilisateurById();
      })
      .catch((error) => {
        toast.error('Erreur lors de la réinitialisation du 2FA');
        console.error(error);
      })
      .finally(() => setIsResetting2FA(false));
  };

  if (isLoading || partenaireLoading || roleLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen-fit">
        <IconSpinner className="animate-spin w-10 h-10"/>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen-fit p-4 animate-in fade-in duration-300">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">{id !== undefined ? 'Modification' : 'Création'} d'un utilisateur</h3>
        </div>

        {(isAdmin) && (
          <div className="flex gap-4">
            <button
              onClick={() => navigate('/parametrage/utilisateurs')}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
            >
              <IconTurnBack/>
              Retour
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm mx-auto w-5/6 px-5">
        <form className="grid grid-cols-2 gap-3 p-5" onSubmit={formik.handleSubmit}>
          <div>
            <label className="form-label">Nom</label>
            <input
              name="nom"
              className="form-input flex-1"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.nom}
            />
            {formik.errors.nom ? <div className="text-xs text-red-600">
              {formik.errors.nom}
            </div> : null}
          </div>
          <div>
            <label className="form-label">Prénom</label>
            <input
              name="prenom"
              className="form-input flex-1"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.prenom}
            />
            {formik.errors.prenom ? <div className="text-xs text-red-600">
              {formik.errors.prenom}
            </div> : null}
          </div>
          <div>
            <label className="form-label">E-mail</label>
            <input
              name="email"
              className="form-input"
              type="email"
              placeholder="@gmail.com"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email}
            />
            {formik.errors.email ? <div className="text-xs text-red-600">
              {formik.errors.email}
            </div> : null}
          </div>
          <div>
            <label className="form-label">Nom d'utilisateur</label>
            <input
              name="nomUtilisateur"
              className="form-input"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.nomUtilisateur}
            />
            {formik.errors.nomUtilisateur ? <div className="text-xs text-red-600">
              {formik.errors.nomUtilisateur}
            </div> : null}
          </div>
          <div className="col-span-2">
            <label className="form-label">Rôle</label>
            <select
              name="utilisateurRole"
              className={`w-full form-input form-select ${(!isAdmin) ? 'hover:cursor-not-allowed' : ''}`}
              disabled={roleLoading || !isAdmin}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.utilisateurRole}
            >
              <option value=""></option>
              {utilisateurRoles.map((role, key: number) =>
                <option key={key} value={role.id}>{role.libelle}</option>
              )}
            </select>
            {formik.errors.utilisateurRole ? <div className="text-xs text-red-600">
              {formik.errors.utilisateurRole}
            </div> : null}
          </div>
          {displayPartenaire ?
            <div className="col-span-2">
              <label className="form-label">Partenaire</label>
              <div className={`${(!isAdmin) ? 'hover:cursor-not-allowed' : ''}`}>
                <Select<Option | string, true>
                  name="partenaire"
                  className="w-full form-input-select2"
                  classNamePrefix="form-input-select2"
                  placeholder=""
                  isMulti
                  unstyled
                  options={partenaireData}
                  isDisabled={partenaireLoading || !isAdmin}
                  onChange={option => formik.setFieldValue("partenaire", option.length > 0 ? option : "")}
                  value={formik.values.partenaire}
                />
              </div>
              {formik.errors.partenaire ? <div className="text-xs text-red-600">
                {formik.errors.partenaire}
              </div> : null}
            </div>
            : null}
          <div className="col-span-2">
            <label className="form-label">Mot de passe</label>
            <div className="relative">
              <input
                name="password"
                className="form-input"
                type={`${showPassword ? "text" : "password"}`}
                placeholder="••••••••"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.password}
                autoComplete="new-password"
              />
              <span
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-2.5 cursor-pointer select-none"
              >
                {showPassword ? <IconEye className="w-4 h-4 text-gray-400"/> : <IconEyeSlash className="w-4 h-4 text-gray-400"/>}
              </span>
            </div>
            {formik.errors.password ? <div className="text-xs text-red-600">
              {formik.errors.password}
            </div> : null}
          </div>
          <div className="col-span-2">
            <label className="form-label">Confirmation du mot de passe</label>
            <div className="relative">
              <input
                name="confirmPassword"
                className="form-input"
                type={`${showConfirm ? "text" : "password"}`}
                placeholder="••••••••"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.confirmPassword}
                autoComplete="new-password"
              />
              <span
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-2.5 cursor-pointer select-none"
              >
                {showConfirm ? <IconEye className="w-4 h-4 text-gray-400"/> : <IconEyeSlash className="w-4 h-4 text-gray-400"/>}
              </span>
            </div>
            {formik.errors.confirmPassword ? <div className="text-xs text-red-600">
              {formik.errors.confirmPassword}
            </div> : null}
          </div>
          {isAdmin && id && (
            <div className="col-span-2">
              <TwoFactorSettings
                utilisateur={utilisateur}
                showReset2FAConfirm={showReset2FAConfirm}
                setShowReset2FAConfirm={setShowReset2FAConfirm}
                handleReset2FA={handleReset2FA}
                isResetting2FA={isResetting2FA}
                onChange={(data) => setSettings2FA(data)}
              />
            </div>
          )}
          <div className="col-span-2 flex justify-center pt-4">
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
            >
              {isSubmitting && (
                <span className="flex gap-2">
                  <IconSpinner className="animate-spin w-4 h-4 mt-1"/>
                  Enregistrement ...
                </span>
              )}
              {!isSubmitting && (
                <span className="flex gap-2">
                  <IconCheck className="w-4 h-4 mt-1"/>
                  Enregistrer
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UtilisateurForm;